import { useEffect, useState } from 'react';
import MentorshipService, { MentorshipDodPreferences } from './MentorshipService';

interface MentorshipDefinitionOfDoneProps {
    cohortId: number | null;
    disabled?: boolean;
}

const JIRA_DEFAULTS: MentorshipDodPreferences = {
    interactionsEnabled: true,
    interactionsMinimum: 1,
    oneToOneSessionsEnabled: true,
    oneToOneSessionsMinimum: 1,
    showDodProgressToMentors: true,
    showDodProgressToMentees: false,
    alertAdminWhenPairFallingBehind: true,
};

const cloneConfig = (value: MentorshipDodPreferences): MentorshipDodPreferences => ({
    interactionsEnabled: value.interactionsEnabled === true,
    interactionsMinimum: Number(value.interactionsMinimum),
    oneToOneSessionsEnabled: value.oneToOneSessionsEnabled === true,
    oneToOneSessionsMinimum: Number(value.oneToOneSessionsMinimum),
    showDodProgressToMentors: value.showDodProgressToMentors === true,
    showDodProgressToMentees: value.showDodProgressToMentees === true,
    alertAdminWhenPairFallingBehind: value.alertAdminWhenPairFallingBehind === true,
});

const configEqual = (a: MentorshipDodPreferences, b: MentorshipDodPreferences) =>
    a.interactionsEnabled === b.interactionsEnabled &&
    a.interactionsMinimum === b.interactionsMinimum &&
    a.oneToOneSessionsEnabled === b.oneToOneSessionsEnabled &&
    a.oneToOneSessionsMinimum === b.oneToOneSessionsMinimum &&
    a.showDodProgressToMentors === b.showDodProgressToMentors &&
    a.showDodProgressToMentees === b.showDodProgressToMentees &&
    a.alertAdminWhenPairFallingBehind === b.alertAdminWhenPairFallingBehind;

const parseMinimumInput = (raw: string): number => {
    if (raw === '') return 0;
    const n = Number(raw);
    return Number.isFinite(n) ? n : 0;
};

const MentorshipDefinitionOfDone = ({ cohortId, disabled = false }: MentorshipDefinitionOfDoneProps) => {
    const cohortAvailable = cohortId != null && !disabled;
    const [loaded, setLoaded] = useState<MentorshipDodPreferences | null>(null);
    const [draft, setDraft] = useState<MentorshipDodPreferences>(JIRA_DEFAULTS);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    useEffect(() => {
        if (!cohortAvailable || cohortId == null) {
            setLoaded(null);
            setDraft(JIRA_DEFAULTS);
            setErrorMessage('');
            setSuccessMessage('');
            setIsLoading(false);
            return;
        }

        let cancelled = false;
        const load = async () => {
            setIsLoading(true);
            setErrorMessage('');
            setSuccessMessage('');
            try {
                const next = await MentorshipService.getDodPreferences(cohortId);
                if (cancelled) return;
                const cloned = cloneConfig(next);
                setLoaded(cloned);
                setDraft(cloneConfig(next));
            } catch (error) {
                if (cancelled) return;
                setLoaded(null);
                setDraft(JIRA_DEFAULTS);
                setErrorMessage(MentorshipService.getErrorMessage(error));
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [cohortAvailable, cohortId]);

    const dirty = loaded != null && !configEqual(draft, loaded);
    const actionsDisabled = !cohortAvailable || isLoading || isSaving || loaded == null;

    const handleSave = async () => {
        if (cohortId == null || actionsDisabled || !dirty) return;
        setIsSaving(true);
        setErrorMessage('');
        setSuccessMessage('');
        try {
            const saved = await MentorshipService.saveDodPreferences(cohortId, draft);
            const cloned = cloneConfig(saved);
            setLoaded(cloned);
            setDraft(cloneConfig(saved));
            setSuccessMessage('Settings saved.');
        } catch (error) {
            setErrorMessage(MentorshipService.getErrorMessage(error));
        } finally {
            setIsSaving(false);
        }
    };

    const handleDiscard = () => {
        if (loaded == null) return;
        setDraft(cloneConfig(loaded));
        setErrorMessage('');
        setSuccessMessage('');
    };

    const toggle = (
        key:
            | 'interactionsEnabled'
            | 'oneToOneSessionsEnabled'
            | 'showDodProgressToMentors'
            | 'showDodProgressToMentees'
            | 'alertAdminWhenPairFallingBehind'
    ) => {
        setDraft((current) => ({ ...current, [key]: !current[key] }));
        setSuccessMessage('');
    };

    const setMinimum = (key: 'interactionsMinimum' | 'oneToOneSessionsMinimum', raw: string) => {
        setDraft((current) => ({ ...current, [key]: parseMinimumInput(raw) }));
        setSuccessMessage('');
    };

    return (
        <div>
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                <h3 className="font-20 font-600 mb-0">Definition of Done</h3>
                <div className="d-flex flex-wrap gap-2">
                    <button type="button" className="btn btn-success" disabled={actionsDisabled || !dirty} onClick={handleSave}>
                        Save Settings
                    </button>
                    <button
                        type="button"
                        className="btn btn-outline-secondary"
                        disabled={actionsDisabled || !dirty}
                        onClick={handleDiscard}>
                        Discard
                    </button>
                </div>
            </div>

            {errorMessage && <div className="alert alert-danger">{errorMessage}</div>}
            {successMessage && <div className="alert alert-success">{successMessage}</div>}

            {!cohortAvailable && <div className="text-muted mb-3">Select a cohort to manage Definition of Done.</div>}
            {cohortAvailable && isLoading && <div className="text-muted mb-3">Loading Definition of Done settings...</div>}

            {cohortAvailable && !isLoading && loaded != null && (
                <fieldset disabled={isSaving}>
                    <div className="card border-0 rounded-3 shadow-sm bg-white mb-3">
                        <div className="card-header bg-white border-bottom py-3 px-4 d-flex justify-content-between align-items-center">
                            <div className="card-title mb-0 font-16 font-700">📊 Milestone Configuration</div>
                            <div className="font-14 text-secondary">All active metrics must be met for a pair to be marked "Complete"</div>
                        </div>
                        <div className="card-body p-4 d-flex flex-column gap-3">
                            <div className="card border rounded-3 p-3">
                                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
                                    <div className="d-flex align-items-center gap-2">
                                        <div className="form-check form-switch fs-5 mb-0">
                                            <input
                                                id="mentorship-dod-interactions"
                                                className="form-check-input"
                                                type="checkbox"
                                                role="switch"
                                                checked={draft.interactionsEnabled}
                                                onChange={() => toggle('interactionsEnabled')}
                                            />
                                        </div>
                                        <div>
                                            <div className="font-13 font-700">💬 Total Interactions</div>
                                            <div className="font-12 text-secondary">
                                                This counts message conversations on the system and interactions logged manually
                                                outside the system.
                                            </div>
                                        </div> 
                                    </div>
                                    <div className="d-flex align-items-center gap-2">
                                        <span className="font-13 text-secondary">Minimum:</span>
                                        <input
                                            id="mentorship-dod-interactions-min"
                                            type="number"
                                            className="form-control form-control-sm text-center font-13"
                                            style={{ width: '70px' }}
                                            min={1}
                                            max={20}
                                            step={1}
                                            disabled={!draft.interactionsEnabled}
                                            value={draft.interactionsMinimum}
                                            onChange={(event) => setMinimum('interactionsMinimum', event.target.value)}
                                        />

                                        <span className="font-13 text-secondary">1–20</span>
                                    </div>
                                </div>
                                <div className="bg-light rounded-3 p-2 font-14 text-secondary">
                                    ✅ <strong>Counts towards this metric:</strong> Platform messages, manually logged
                                    WhatsApp/LinkedIn/in-person interactions
                                </div>
                            </div>

                            <div className="card border rounded-3 p-3">
                                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
                                    <div className="d-flex align-items-center gap-2">
                                        <div className="form-check form-switch fs-5 mb-0">
                                            <input
                                                id="mentorship-dod-sessions"
                                                className="form-check-input"
                                                type="checkbox"
                                                role="switch"
                                                checked={draft.oneToOneSessionsEnabled}
                                                onChange={() => toggle('oneToOneSessionsEnabled')}
                                            />
                                        </div>
                                        <div>
                                            <div className="font-13 font-700">📅 One-to-One Sessions</div>
                                            <div className="font-12 text-secondary">
                                                This counts one-to-one sessions through the module and sessions logged manually
                                                outside the platform.
                                            </div>
                                        </div>
                                    </div>
                                    <div className="d-flex align-items-center gap-2">
                                        <span className="font-13 text-secondary">Minimum:</span>

                                        <input
                                            id="mentorship-dod-sessions-min"
                                            type="number"
                                            className="form-control form-control-sm text-center font-13"
                                            style={{ width: '70px' }}
                                            min={1}
                                            max={20}
                                            step={1}
                                            disabled={!draft.oneToOneSessionsEnabled}
                                            value={draft.oneToOneSessionsMinimum}
                                            onChange={(event) => setMinimum('oneToOneSessionsMinimum', event.target.value)}
                                        />
                                        <span className="font-13 text-secondary">1–20</span>
                                    </div>
                                </div>
                                <div className="bg-light rounded-3 p-2 font-12 text-secondary">
                                    ✅ <strong>Counts towards this metric:</strong> Platform sessions, manually logged video/phone calls,
                                    campus visits
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="card border-0 rounded-3 shadow-sm bg-white">
                        <div className="card-header bg-white border-bottom py-3 px-4">
                            <div className="card-title mb-0 font-16 font-700">📈 Completion Tracking Settings</div>
                        </div>
                        <div className="card-body p-4">
                            <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
                                <div>
                                    
                                    <label className="font-13 font-700 text-dark" htmlFor="mentorship-dod-show-mentors">Show DoD progress to mentors</label>
                                    <div className="font-13 text-secondary">Mentors see, for each assigned mentee, whether the Done criteria are met.
</div>
                                </div>
                                <div className="form-check form-switch fs-5 mb-0">
                                    <input
                                        id="mentorship-dod-show-mentors"
                                        className="form-check-input"
                                        type="checkbox"
                                        role="switch"
                                        checked={draft.showDodProgressToMentors}
                                        onChange={() => toggle('showDodProgressToMentors')}
                                    />
                                </div>
                            </div>
                            <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
                                <div>
                                    <label className="font-13 font-700 text-dark" htmlFor="mentorship-dod-show-mentees">Show DoD progress to mentees</label>
                                    <div className="font-13 text-secondary">Mentees see whether they have the required interactions with the mentor.</div>
                                </div>
                                <div className="form-check form-switch fs-5 mb-0">
                                    <input
                                        id="mentorship-dod-show-mentees"
                                        className="form-check-input"
                                        type="checkbox"
                                        role="switch"
                                        checked={draft.showDodProgressToMentees}
                                        onChange={() => toggle('showDodProgressToMentees')}
                                    />
                                </div>
                            </div>
                            <div className="d-flex justify-content-between align-items-center py-2">
                                <div>
                                    
                                    <label className="font-13 font-700 text-dark" htmlFor="mentorship-dod-admin-alert">Alert Institute Admin when a pair is falling behind</label>
                                    <div className="font-13 text-secondary">Flag pairs with zero interactions after 45 days of cohort being live</div>
                                </div>
                                <div className="form-check form-switch fs-5 mb-0">
                                    <input
                                        id="mentorship-dod-admin-alert"
                                        className="form-check-input"
                                        type="checkbox"
                                        role="switch"
                                        checked={draft.alertAdminWhenPairFallingBehind}
                                        onChange={() => toggle('alertAdminWhenPairFallingBehind')}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    

                    

                    
                </fieldset>
            )}
        </div>
    );
};

export default MentorshipDefinitionOfDone;
